import translations from './.vocab';
import { useTranslations } from '@vocab/react';
import { generateTranslationTypeKey } from '../utils';
import { useSearchResultsState } from '../state';
import { useSearchParams } from '../search-params';
import type { SearchParams } from '@seek/chalice-types';

export const useCommaFormattedClassification = (searchParams: SearchParams) => {
  const numSearchParams = Object.keys(searchParams).length;
  const numSubclassifications =
    searchParams?.subclassification?.split(',').length || 0;
  const numParentClassifications =
    searchParams?.classification?.split(',').length || 0;
  const isLocationOrWorktypeSelected =
    Boolean(searchParams?.worktype) || Boolean(searchParams?.where);

  return (
    numSearchParams === 3 &&
    numSubclassifications === 1 &&
    numParentClassifications === 1 &&
    isLocationOrWorktypeSelected
  );
};

export const useSearchResultsHeadDescription = ({
  isCompanySearch = false,
}: {
  isCompanySearch: boolean;
}): string => {
  const { t } = useTranslations(translations);
  const searchParams = useSearchParams();
  const shouldUseCommaFormattedClassification =
    useCommaFormattedClassification(searchParams);
  const {
    brand,
    jobCount,
    workTypeDescription,
    classDescription,
    keywords,
    locationDescription,
  } = useSearchResultsState({
    classRemainderFn: (classificationCount) =>
      ` ${t('additional-classifications-description', {
        classificationCount,
      })}`, // Be wary of the space prefix here
    commaFormattedClassification: shouldUseCommaFormattedClassification,
  });
  const key: string = generateTranslationTypeKey({
    isCompanySearch,
    searchParams,
    classDescription,
    fallbackKey: 'noparams-description',
  });
  const pageDescription: string = useDescription({
    key,
    brand,
    jobCount,
    keywords,
    workTypeDescription,
    classDescription,
    locationDescription,
  });

  return pageDescription;
};

const useDescription = ({
  key,
  brand,
  jobCount,
  keywords,
  workTypeDescription,
  classDescription,
  locationDescription,
}: {
  key: string;
  brand: string;
  jobCount: number;
  keywords: string;
  workTypeDescription: string;
  classDescription: string;
  locationDescription: string;
}): string => {
  const { t } = useTranslations(translations);
  const country = t('country-description');

  const descriptionTypes = {
    worktype: t('worktype-description', {
      brand,
      jobCount,
      workTypeDescription,
      country,
    }),
    multiworktype: t('multiworktype-description', { brand, jobCount, country }),
    'classification-worktype': t('classification-worktype-description', {
      brand,
      jobCount,
      workTypeDescription,
      classDescription,
      country,
    }),
    salarytype: t('salarytype-description', { brand, jobCount, country }),
    'salaryrange-salarytype': t('salaryrange-salarytype-description', {
      brand,
      jobCount,
      country,
    }),
    daterange: t('daterange-description', { brand, jobCount, country }),
    keywords: t('keywords-description', { brand, jobCount, country, keywords }),
    'keywords-location': t('keywords-location-description', {
      brand,
      jobCount,
      locationDescription,
      keywords,
    }),
    'keywords-worktype': t('keywords-worktype-description', {
      brand,
      jobCount,
      workTypeDescription,
      keywords,
      country,
    }),
    'keywords-multiworktype': t('keywords-multiworktype-description', {
      brand,
      jobCount,
      keywords,
      country,
    }),
    'classification-keywords': t('classification-keywords', {
      brand,
      jobCount,
      classDescription,
      keywords,
      country,
    }),
    location: t('location-description', {
      locationDescription,
      brand,
      jobCount,
    }),
    'location-worktype': t('location-worktype-description', {
      locationDescription,
      brand,
      jobCount,
    }),
    classification: t('classification-description', {
      classDescription,
      brand,
      jobCount,
      country,
    }),
    'classification-location': t('classification-location-description', {
      locationDescription,
      classDescription,
      brand,
      jobCount,
    }),
  };

  const noParamsDescription = t('noparams-description', {
    country,
    brand,
    jobCount,
  });

  return (
    descriptionTypes[key as keyof typeof descriptionTypes] ||
    noParamsDescription
  );
};
